import './Instructions.scss';
import { LogoSection } from '../../../Components/LogoSection';
import useResponsive from '../../../hooks/useResponsive';
import { Subheader, Subtitle, Title } from '../../../Components/Typography';
import { Button } from '../../../Components/Button';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

interface Item {
  id: number;
  title: string;
  content?: string;
}

export const Instructions = () => {
  const navigate = useNavigate();
  const { isDesktopOrLaptop } = useResponsive();

  const instructions: Item[] = [
    {
      id: 1,
      title: '¿Cómo realizo un pedido de impresión?',
      content:
        'Lo primero que necesitas es tener organizado el material que vas a cargar. Recordá que solo podés adjuntar en formato PDF.\nEs simple, son 3 pasos:',
    },
    {
      id: 2,
      title: 'Paso 1: Archivos y opciones de impresión',
      content:
        'Para cargar los archivos podés "arrastrar" o "adjuntar".\nUna vez cargados los archivos, continuás con las opciones de impresión: tamaño, color, cantidad, etc.\nTenés un recuadro "Notas adicionales" donde podés hacer aclaraciones para tener en cuenta al momento de realizar el pedido.\nYa cargadas todas las opciones, sobre tu derecha ves el detalle y cerrás seleccionando "Realizar pedido".',
    },
    {
      id: 3,
      title: 'Paso 2: Opciones de envío',
      content:
        '• Retiro por local\nPuede ser Sucursal "San Justo" o Sucursal "Haedo".\nTe vamos a pasar un código para que puedas retirar por la sucursal elegida cuando te avisemos que tu trabajo está terminado.\n• Envío a domicilio\nRecordá verificar que los datos estén correctos. Hay un recuadro de "Notas adicionales" para cualquier aclaración con respecto al envío.',
    },
    {
      id: 4,
      title: 'Paso 3: Datos de pago',
      content: 'Cargá los datos dependiendo tu forma de pago.',
    },
    { id: 5, title: 'Paso 4: Confirmar y pagar la orden' },
    { id: 6, title: '¡Listo tu pedido!', content: 'Solo queda aguardar.' },
  ];

  const questions: Item[] = [
    {
      id: 1,
      title: '¿Anillado "largo" o anillado "corto"?',
      content:
        'El anillado "largo" sería con el texto en la hoja de forma vertical. El anillado "corto" sería con el texto en la hoja de forma horizontal.',
    },
    {
      id: 2,
      title: '¿El anillado es por pedido o por archivo?',
      content:
        'Todos lo archivos cargados juntos en un pedido se anillan juntos de elegir la opción anillado. Si querés anillar los archivos por separado debes realizar distintos pedidos.',
    },
    {
      id: 3,
      title: '¿Cómo realizar de un archivo múltiples copias?',
      content:
        'Simplemente seleccionas en cantidad de copias lo que necesitas.',
    },
    {
      id: 4,
      title: '¿Por qué no puedo anillar mi pedido?',
      content:
        'Si tu pedido es muy grande para anillar (por ejemplo, más de 500 hojas), el sistema no te permite la opción de anillado. En este caso tenés que realizar el pedido por separado.',
    },
    {
      id: 5,
      title: '¿Por qué no me deja subir un archivo?',
      content:
        'Es posible que el archivo no cumpla con los requisitos del sistema. Recordá que tiene que estar en formato PDF y el tamaño máximo permitido para un archivo es 250MB. Tampoco puede estar protegido con contraseña.',
    },
    {
      id: 6,
      title: '¿Qué pasa que no puedo terminar mi pedido?',
      content:
        'Si el botón "Finalizar pedido" no está disponible, es posible que algún archivo no este cargado 100%.',
    },
    {
      id: 7,
      title: '¿Cómo sé que mi pedido fue tomado?',
      content:
        'Al terminar todos los pasos para realizar tu pedido, se genera un código para que puedas hacer el seguimiento de tu pedido. Te recomendamos que no lo pierdas.',
    },
    {
      id: 8,
      title: '¿Qué métodos de pago aceptan?',
      content:
        'Aceptamos tarjeta de crédito o debito. Los pagos son procesados a través de Mercado Pago. La información de sus tarjetas no pasa por nuestro sistema.',
    },
    {
      id: 9,
      title: '¿Cómo puedo contactarme?',
      content:
        'En el caso de que no puedas resolver algo o necesites ayuda podés enviarnos Whatsapp al 1159270888',
    },
    {
      id: 10,
      title: '¿Qué debo revisar al pedir material anillado?',
      content:
        'Al momento de pedir material que va anillado, revisá bien si el texto está para leer en orientación HORIZONTAL o VERTICAL.',
    },
  ];

  const [visibleAnswers, setVisibleAnswers] = useState<Set<number>>(new Set());

  const toggleAnswer = (id: number) => {
    setVisibleAnswers((prev) => {
      const newVisibleAnswers = new Set(prev);
      if (newVisibleAnswers.has(id)) {
        newVisibleAnswers.delete(id);
      } else {
        newVisibleAnswers.add(id);
      }
      return newVisibleAnswers;
    });
  };

  const onClickVolver = () => {
    navigate(-1);
  };

  return (
    <div
      className={
        isDesktopOrLaptop ? 'instructions-desktop' : 'instructions-mobile'
      }
    >
      <LogoSection showInstructions={false} />

      <div className="main-column">
        <div className="left-content">
          <Button onClick={onClickVolver}>Volver</Button>
          <div className="title-section">
            <Title>Bienvenidos a Centro de Copiado EL RAYO</Title>
          </div>
          <div className="notes-section">
            <Subheader light secondary>
              Antes de comenzar te dejamos las instrucciones para que puedas
              usar nuestro sistema de impresiones.
            </Subheader>
            {instructions.map((instruction) => (
              <p key={instruction.id}>
                <Subheader>{instruction.title}</Subheader>
                <br />
                {instruction.content?.split('\n').map((line, index) => (
                  <Subheader light secondary key={index}>
                    {line}
                    <br />
                  </Subheader>
                ))}
              </p>
            ))}
          </div>
          <br />
          <div className="title-section">
            <Subtitle>Preguntas frecuentes</Subtitle>
          </div>
          <div className="notes-section">
            {questions.map((question) => (
              <p className="question" key={question.id}>
                <Subheader
                  className="text-toggle"
                  onClick={() => toggleAnswer(question.id)}
                >
                  {question.title}
                </Subheader>
                <br />
                {visibleAnswers.has(question.id) && (
                  <Subheader light secondary>
                    {question.content}
                  </Subheader>
                )}
              </p>
            ))}
          </div>
          <br />
          <div className="title-section">
            <Subtitle>Notas importantes</Subtitle>
          </div>
          <div className="notes-section" style={{ marginBottom: '4rem' }}>
            <Subheader light secondary>
              • Los datos personales son utilizados únicamente para procesar tu
              pedido. No compartimos tus datos con terceros y son almacenados de
              forma segura en nuestro sistema. La información de tus tarjetas
              nunca pasa por nuestro sistema, ya que es procesada directamente
              por Mercado Pago.
            </Subheader>
            <br />
            <p style={{ marginTop: '-10px' }}></p>
            <Subheader light secondary>
              • Si necesitás que los archivos tengan un orden al imprimirlos,
              asegurate de que el archivo tenga el número de orden delante del
              nombre (por ejemplo: "1 - nombre del archivo"). Así, al momento de
              la descarga, quedan organizados.
            </Subheader>
          </div>
        </div>
      </div>
    </div>
  );
};
